import { getContext, setContext } from 'svelte';
import { GrpcWebFetchTransport } from '@protobuf-ts/grpcweb-transport';
import { QueryClient, setQueryClientContext } from '$lib/api/tanstack-query';

import { AgentSubsystemsServiceClient } from '@viamrobotics/web-client/agentsubsystems/v1/agentsubsystems.client';
import { BuildServiceClient } from '@viamrobotics/web-client/app/build/v1/build.client';
import { DataServiceClient } from '@viamrobotics/web-client/app/data/v1/data.client';
import { DataPipelinesServiceClient } from '@viamrobotics/web-client/app/datapipelines/v1/data_pipelines.client';
import { DatasetServiceClient } from '@viamrobotics/web-client/app/dataset/v1/dataset.client';
import { MLInferenceServiceClient } from '@viamrobotics/web-client/app/mlinference/v1/ml_inference.client';
import { MLTrainingServiceClient } from '@viamrobotics/web-client/app/mltraining/v1/ml_training.client';
import { PackageServiceClient } from '@viamrobotics/web-client/app/packages/v1/packages.client';
import { AppServiceClient } from '@viamrobotics/web-client/app/v1/app.client';
import { BillingServiceClient as BillingSummaryClient } from '@viamrobotics/web-client/app/v1/billing.client';
import { EndUserServiceClient } from '@viamrobotics/web-client/app/v1/end_user.client';
import { BillingServiceClient } from '@viamrobotics/web-client/billing/v1/billing.client';
import { BillingAdministrationServiceClient } from '@viamrobotics/web-client/billing/v1/billingadministration.client';
import { DashboardServiceClient } from '@viamrobotics/web-client/dashboard/v1/dashboard.client';
import { InternalDataAnnotationsServiceClient } from '@viamrobotics/web-client/dataannotations/v1/dataannotations.client';
import { InternalDataServiceClient } from '@viamrobotics/web-client/datamanagement/internalapi/v1/data.client';
import { InternalDataPipelinesServiceClient } from '@viamrobotics/web-client/datapipelines/v1/datapipelines.client';
import { FavoriteDoCommandServiceClient } from '@viamrobotics/web-client/docommand/v1/do_command.client';
import { InternalEnvironmentServiceClient } from '@viamrobotics/web-client/environment/v1/environment.client';
import { FeatureFlagServiceClient } from '@viamrobotics/web-client/feature/v1/feature.client';
import { FleetDashboardServiceClient } from '@viamrobotics/web-client/fleetdashboard/v1/fleetdashboard.client';
import { InferenceServiceClient } from '@viamrobotics/web-client/inference/v1/inference.client';
import { MotionPlanServiceClient } from '@viamrobotics/web-client/motionplan/v1/motionplan.client';
import { LogServiceClient } from '@viamrobotics/web-client/log/v1/log.client';
import { InternalMLInferenceServiceClient } from '@viamrobotics/web-client/mlinference/internalapi/v1/mlinference.client';
import { InternalMLTrainingServiceClient } from '@viamrobotics/web-client/mltraining/internalapi/v1/mltraining.client';
import { RdkBuiltinsServiceClient } from '@viamrobotics/web-client/rdkbuiltins/v1/rdkbuiltins.client';
import { RegistryServiceClient } from '@viamrobotics/web-client/registry/v1/registry.client';
import { ReportingServiceClient } from '@viamrobotics/web-client/reporting/v1/reporting.client';
import { RobotReservationServiceClient } from '@viamrobotics/web-client/robotreservation/v1/robot_reservation.client';
import { TeleopServiceClient } from '@viamrobotics/web-client/teleop/v1/teleop.client';
import { InternalUserServiceClient } from '@viamrobotics/web-client/user/v1/user.client';
import { InternalAuthServiceClient } from '@viamrobotics/web-client/auth/v1/auth.client';

import { browser } from '$app/environment';

import type { AuthContext } from '$lib/auth';

import {
  createLoginMethodDeniedMutationCache,
  createLoginMethodDeniedQueryCache,
  shouldRetryQueryOnError,
} from './login-method-denied-handler';

const SERVICE_CLIENTS_CONTEXT_KEY = Symbol('api-service-clients');

export type { DataPipelinesServiceClient } from '@viamrobotics/web-client/app/datapipelines/v1/data_pipelines.client';
export type { AppServiceClient } from '@viamrobotics/web-client/app/v1/app.client';
export type { DataServiceClient } from '@viamrobotics/web-client/app/data/v1/data.client';
export type { InternalDataServiceClient } from '@viamrobotics/web-client/datamanagement/internalapi/v1/data.client';
export type { InternalDataPipelinesServiceClient } from '@viamrobotics/web-client/datapipelines/v1/datapipelines.client';
export type { FeatureFlagServiceClient } from '@viamrobotics/web-client/feature/v1/feature.client';
export type { FleetDashboardServiceClient } from '@viamrobotics/web-client/fleetdashboard/v1/fleetdashboard.client';
export type { InternalUserServiceClient } from '@viamrobotics/web-client/user/v1/user.client';

export interface APIClients {
  queryClient: QueryClient;
  serviceClients: APIServiceClients;
}

export interface APIServiceClients {
  app: AppServiceClient;
  build: BuildServiceClient;
  data: DataServiceClient;
  billing: BillingServiceClient;
  billingSummaryService: BillingSummaryClient;
  billingAdministration: BillingAdministrationServiceClient;
  internalDataAnnotations: InternalDataAnnotationsServiceClient;
  internalMLInference: InternalMLInferenceServiceClient;
  internalMLTraining: InternalMLTrainingServiceClient;
  endUser: EndUserServiceClient;
  inference: InferenceServiceClient;
  motionPlan: MotionPlanServiceClient;
  internalData: InternalDataServiceClient;
  internalUser: InternalUserServiceClient;
  feature: FeatureFlagServiceClient;
  package: PackageServiceClient;
  environment: InternalEnvironmentServiceClient;
  robotReservation: RobotReservationServiceClient;
  rdkBuiltins: RdkBuiltinsServiceClient;
  agentSubsystems: AgentSubsystemsServiceClient;
  mlInference: MLInferenceServiceClient;
  mlTraining: MLTrainingServiceClient;
  dataset: DatasetServiceClient;
  fleetDashboard: FleetDashboardServiceClient;
  teleop: TeleopServiceClient;
  dashboard: DashboardServiceClient;
  favoriteDoCommand: FavoriteDoCommandServiceClient;
  registryAdmin: RegistryServiceClient;
  log: LogServiceClient;
  reporting: ReportingServiceClient;
  dataPipelines: DataPipelinesServiceClient;
  internalDataPipelines: InternalDataPipelinesServiceClient;
  internalAuth: InternalAuthServiceClient;
}

/**
 * Create a context to store query data and make gRPC calls.
 *
 * The transport is built with the supplied `authContext`'s auth interceptor
 * installed, so every unary and server-streaming call gets the current Bearer
 * token attached and unary calls transparently retry once on UNAUTHENTICATED.
 * Call sites don't need to thread `rpcOptions` through the request — that
 * pattern stays working in parallel for the cleanup sweep, but new code
 * should rely on the transport-level interceptor.
 */
export const createAPIClients = (authContext: AuthContext): APIClients => {
  const queryClient = new QueryClient({
    queryCache: createLoginMethodDeniedQueryCache(authContext.logout),
    mutationCache: createLoginMethodDeniedMutationCache(authContext.logout),
    defaultOptions: {
      queries: {
        enabled: browser,
        retry: shouldRetryQueryOnError,
      },
    },
  });
  const serviceClients = {} as APIServiceClients;

  if (browser) {
    const baseUrl = window.location.origin;
    const transport = new GrpcWebFetchTransport({
      baseUrl,
      interceptors: [authContext.createAuthInterceptor()],
    });

    serviceClients.agentSubsystems = new AgentSubsystemsServiceClient(
      transport
    );
    serviceClients.billing = new BillingServiceClient(transport);
    serviceClients.billingAdministration =
      new BillingAdministrationServiceClient(transport);
    serviceClients.app = new AppServiceClient(transport);
    serviceClients.build = new BuildServiceClient(transport);
    serviceClients.data = new DataServiceClient(transport);
    serviceClients.endUser = new EndUserServiceClient(transport);
    serviceClients.inference = new InferenceServiceClient(transport);
    serviceClients.motionPlan = new MotionPlanServiceClient(transport);
    serviceClients.internalDataAnnotations =
      new InternalDataAnnotationsServiceClient(transport);
    serviceClients.internalMLInference = new InternalMLInferenceServiceClient(
      transport
    );
    serviceClients.internalMLTraining = new InternalMLTrainingServiceClient(
      transport
    );
    serviceClients.internalData = new InternalDataServiceClient(transport);
    serviceClients.internalUser = new InternalUserServiceClient(transport);
    serviceClients.feature = new FeatureFlagServiceClient(transport);
    serviceClients.package = new PackageServiceClient(transport);
    serviceClients.environment = new InternalEnvironmentServiceClient(
      transport
    );
    serviceClients.rdkBuiltins = new RdkBuiltinsServiceClient(transport);
    serviceClients.robotReservation = new RobotReservationServiceClient(
      transport
    );
    serviceClients.mlTraining = new MLTrainingServiceClient(transport);
    serviceClients.mlInference = new MLInferenceServiceClient(transport);
    serviceClients.dataset = new DatasetServiceClient(transport);
    serviceClients.fleetDashboard = new FleetDashboardServiceClient(transport);
    serviceClients.teleop = new TeleopServiceClient(transport);
    serviceClients.dashboard = new DashboardServiceClient(transport);
    serviceClients.favoriteDoCommand = new FavoriteDoCommandServiceClient(
      transport
    );
    serviceClients.registryAdmin = new RegistryServiceClient(transport);
    serviceClients.log = new LogServiceClient(transport);
    serviceClients.reporting = new ReportingServiceClient(transport);
    serviceClients.dataPipelines = new DataPipelinesServiceClient(transport);
    serviceClients.internalDataPipelines =
      new InternalDataPipelinesServiceClient(transport);
    serviceClients.billingSummaryService = new BillingSummaryClient(transport);
    serviceClients.internalAuth = new InternalAuthServiceClient(transport);
  }

  return { queryClient, serviceClients };
};

/**
 * Provide an API client and set the QueryClient context.
 *
 * This replaces the need for a `<QueryClientProvider>` component.
 * This provider should be set before all other providers.
 */
export const provideAPIClients = (
  queryClient: QueryClient,
  serviceClients: APIServiceClients
): void => {
  setQueryClientContext(queryClient);
  setContext<APIServiceClients>(SERVICE_CLIENTS_CONTEXT_KEY, serviceClients);
};

const getServiceClientsContext = (): APIServiceClients => {
  return getContext<APIServiceClients>(SERVICE_CLIENTS_CONTEXT_KEY);
};

/**
 * Get an RPC API client to make service calls.
 *
 * @param clientType - the API client you need.
 * @returns the client from context.
 */
export const useClient = <ClientT extends keyof APIServiceClients>(
  clientType: ClientT
): APIServiceClients[ClientT] => {
  return getServiceClientsContext()[clientType];
};

/**
 * Get a RPC API client to make billing service calls.
 *
 * @returns the client from context.
 */
export const useBillingClient = (): BillingServiceClient => {
  return getServiceClientsContext().billing;
};

/**
 * Get a RPC API client to make app billing summary service calls.
 *
 * @returns the client from context.
 */
export const useBillingSummaryClient = (): BillingSummaryClient => {
  return getServiceClientsContext().billingSummaryService;
};

/**
 * Get a RPC API client to make billing administration service calls.
 *
 * @returns the client from context.
 */
export const useBillingAdministrationClient =
  (): BillingAdministrationServiceClient => {
    return getServiceClientsContext().billingAdministration;
  };

/**
 * Get a RPC API client to make app service calls.
 *
 * @returns the client from context.
 */
export const useAPIClient = (): AppServiceClient => {
  return getServiceClientsContext().app;
};

/**
 * Get a RPC API client to make build service calls.
 *
 * @returns the client from context.
 */
export const useBuildClient = (): BuildServiceClient => {
  return getServiceClientsContext().build;
};

/**
 * Get a RPC API client to make app service calls.
 *
 * @returns the client from context.
 */
export const useDataClient = (): DataServiceClient => {
  return getServiceClientsContext().data;
};

/**
 * Get a RPC API client to make internal data service calls.
 *
 * @returns the client from context.
 */
export const useInternalDataClient = (): InternalDataServiceClient => {
  return getServiceClientsContext().internalData;
};

/**
 * Get a RPC API client to make internal data annotations service calls.
 *
 * @returns the client from context.
 */
export const useDataAnnotationsClient =
  (): InternalDataAnnotationsServiceClient => {
    return getServiceClientsContext().internalDataAnnotations;
  };

/**
 * Get a RPC API client to make internal ML inference service calls.
 *
 * @returns the client from context.
 */
export const useInternalMLInferenceClient =
  (): InternalMLInferenceServiceClient => {
    return getServiceClientsContext().internalMLInference;
  };

/**
 * Get a RPC API client to make internal ML training service calls.
 *
 * @returns the client from context.
 */
export const useMLTrainingClient = (): InternalMLTrainingServiceClient => {
  return getServiceClientsContext().internalMLTraining;
};

/**
 * Get a RPC API client to make internal ML inference service calls.
 *
 * @returns the client from context.
 */
export const useMLInferenceClient = (): MLInferenceServiceClient => {
  return getServiceClientsContext().mlInference;
};

/**
 * Get a RPC API client to make internal user service calls.
 *
 * @returns the client from context.
 */
export const useInternalUserClient = (): InternalUserServiceClient => {
  return getServiceClientsContext().internalUser;
};

/**
 * Get a RPC API client to make feature flag service calls.
 *
 * @returns the client from context.
 */
export const useFeatureClient = (): FeatureFlagServiceClient => {
  return getServiceClientsContext().feature;
};

/**
 * Get a RPC API client to make end user service calls.
 *
 * @returns the client from context.
 */
export const useEndUserClient = (): EndUserServiceClient => {
  return getServiceClientsContext().endUser;
};

/**
 * Get a RPC API client to make package service calls.
 *
 * @returns the client from context.
 */
export const usePackageClient = (): PackageServiceClient => {
  return getServiceClientsContext().package;
};

/**
 * Get a RPC API client to make ml training job service calls.
 *
 * @returns the client from context.
 */
export const useTrainingJobClient = (): MLTrainingServiceClient => {
  return getServiceClientsContext().mlTraining;
};

/**
 * Get a RPC API client to make dataset service calls.
 *
 * @returns the client from context.
 */
export const useDatasetClient = (): DatasetServiceClient => {
  return getServiceClientsContext().dataset;
};

/**
 * Get a RPC API client to make robot reservation (try viam) service calls.
 *
 * @returns the client from context.
 */
export const useRobotReservationClient = (): RobotReservationServiceClient => {
  return getServiceClientsContext().robotReservation;
};

/**
 * Get a RPC API client to make fleet dashboard service calls.
 *
 * @returns the client from context.
 */
export const useFleetDashboardClient = (): FleetDashboardServiceClient => {
  return getServiceClientsContext().fleetDashboard;
};

/**
 * Get a RPC API client to make fleet dashboard service calls.
 *
 * @returns the client from context.
 */
export const useTeleopClient = (): TeleopServiceClient => {
  return getServiceClientsContext().teleop;
};

/**
 * Get a RPC API client to make dashboard service calls.
 *
 * @returns the client from context.
 */
export const useDashboardClient = (): DashboardServiceClient => {
  return getServiceClientsContext().dashboard;
};

/**
 * Get a RPC API client to make registry admin service calls.
 *
 * @returns the client from context.
 */
export const useRegistryClient = (): RegistryServiceClient => {
  return getServiceClientsContext().registryAdmin;
};

/**
 * Get a RPC API client to make internal logs calls.
 *
 * @returns the client from context.
 */
export const useLogClient = (): LogServiceClient => {
  return getServiceClientsContext().log;
};

/** Get a RPC API client to make LLM inference service calls. */
export const useInferenceClient = (): InferenceServiceClient => {
  return getServiceClientsContext().inference;
};

/**
 * Get a RPC API client to make data pipelines service calls.
 *
 * @returns the client from context.
 */
export const useDataPipelinesClient = (): DataPipelinesServiceClient => {
  return getServiceClientsContext().dataPipelines;
};

/**
 * Get a RPC API client to make internal data pipelines service calls.
 *
 * @returns the client from context.
 */
export const useInternalDataPipelinesClient =
  (): InternalDataPipelinesServiceClient => {
    return getServiceClientsContext().internalDataPipelines;
  };

/**
 * Get a RPC API client to make internal auth service calls.
 *
 * @returns the client from context.
 */
export const useInternalAuthClient = (): InternalAuthServiceClient => {
  return getServiceClientsContext().internalAuth;
};
